import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { hasPermission, type Permission } from '@oscom/shared';
import { loadSession, type Session } from './session';

/**
 * Session context.
 *
 * `can()` is a convenience for hiding UI the user cannot use. It is NOT a
 * security control - the server checks the same permission again on every call.
 */
interface SessionContextValue {
  session: Session | undefined;
  reload: () => void;
  can: (permission: Permission) => boolean;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }): ReactNode {
  const [session, setSession] = useState<Session | undefined>(undefined);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setSession(undefined);
    void loadSession().then((next) => {
      if (!cancelled) {
        setSession(next);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      reload: () => setReloadToken((token) => token + 1),
      can: (permission) =>
        session?.status === 'authenticated' ? hasPermission(session.me.role, permission) : false,
    }),
    [session],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used inside a SessionProvider');
  }
  return context;
}
